import clsx from 'clsx';
import { KeyboardEvent, useEffect, useRef } from 'react';
import { InputGroup } from 'react-bootstrap';
import DropdownList from './components/DropdownList';
import Input from './components/Input';
import { useInputSelectContext } from './context';
import { FieldProps } from './types';

const InputGroupAutocomplete = ({
	inputConfig,
	mode = 'remote',
	fetchApi,
	urlData,
	className,
	getParams,
	tableColumns,
	getTableData,
	value,
	onChange,
	disabled,
	initialOptions,
}: FieldProps) => {
	const {
		options,
		selectedIndex,
		setSelectedIndex,
		setSelectedOption,
		show,
		setShow,
		setOptions,
		setInitialOptions,
	} = useInputSelectContext();

	const containerRef = useRef<HTMLDivElement>(null);

	const isLocalMode = mode === 'local';

	useEffect(() => {
		if (isLocalMode) {
			const initOptions = getTableData ? getTableData(initialOptions) : initialOptions;
			setOptions(initOptions);
			setInitialOptions(initOptions);
		}

		window.addEventListener('click', checkMouseClickOutSide, true);
		return () => {
			window.removeEventListener('click', checkMouseClickOutSide, true);
		};
	}, []);

	const checkMouseClickOutSide = (e: MouseEvent) => {
		if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
			hidePopover();
		}
	};

	const onRowSelect = (row: any) => {
		setSelectedOption(row);
		hidePopover();
	};

	const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
		switch (e.key) {
			case 'ArrowDown':
				if (!show) {
					setShow(true);
					return;
				}

				setSelectedIndex(selectedIndex >= options.length - 1 ? 0 : selectedIndex + 1);
				break;

			case 'ArrowUp':
				setSelectedIndex(selectedIndex === 0 ? options.length - 1 : selectedIndex - 1);
				break;

			case 'Enter':
				onRowSelect(options[selectedIndex]);
				break;

			case 'Escape':
			case 'Tab':
				hidePopover();
				break;
		}
	};

	const hidePopover = () => {
		setShow(false);
	};

	return (
		<InputGroup
			ref={containerRef}
			className={clsx('w-100 position-relative', className)}
			onKeyDown={onKeyDown}
			aria-haspopup="listbox"
		>
			<Input
				inputConfig={inputConfig}
				getParams={getParams}
				fetchApi={fetchApi}
				urlData={urlData}
				getTableData={getTableData}
				value={value}
				onChange={onChange}
				disabled={disabled}
				isLocalMode={isLocalMode}
			/>

			<DropdownList
				targetElement={containerRef.current}
				containerElement={containerRef.current}
				onRowSelect={onRowSelect}
				columns={tableColumns}
			/>
		</InputGroup>
	);
};

export default InputGroupAutocomplete;
